import { motion, useInView } from "framer-motion";
import { useRef } from "react";

export default function About() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="about"
      data-testid="about-section"
      ref={ref}
      className="relative py-32 md:py-48"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center mb-16"
        >
          <span className="h-rule" />
          <span className="overline">01 — About</span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm group">
              <img
                src="https://customer-assets.emergentagent.com/job_basit-loay-design/artifacts/jecyrl79_photo_2026-07-08_17-16-46.webp"
                alt="Basit Loay — designer at work"
                className="w-full h-full object-cover transition-all duration-[1200ms] scale-105 group-hover:scale-100"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#171717] via-transparent to-transparent" />
              <div className="absolute top-5 left-5 font-mono text-[10px] tracking-[0.28em] text-[#FD1843] uppercase">
                / 001 — Studio · Baghdad
              </div>
            </div>

            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="absolute -bottom-8 -right-4 md:-right-8 bg-[#1c1c1c] border border-white/10 px-6 py-5 rounded-sm"
              style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)" }}
            >
              <div className="font-display text-4xl font-semibold text-white">
                7<span className="text-[#FD1843]">+</span>
              </div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-white/60 uppercase mt-1">
                Years of craft
              </div>
            </motion.div>
          </motion.div>

          {/* Copy */}
          <div className="lg:col-span-7 space-y-10">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.1 }}
              className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-[-0.04em] leading-[1.05]"
            >
              I design{" "}
              <span className="text-[#FD1843] glow-text">premium visuals</span>{" "}
              <br className="hidden md:block" />
              for brands that want to be felt — not just seen.
            </motion.h2>

            {/* English bio removed as requested */}

            {/* Divider */}
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={inView ? { opacity: 1, scaleX: 1 } : {}}
              transition={{ duration: 1, delay: 0.35 }}
              className="w-24 h-px bg-[#FD1843] origin-left"
            />

            {/* Arabic bio */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.4 }}
              dir="rtl"
              lang="ar"
              className="font-arabic space-y-5 text-white/85 text-lg md:text-xl leading-[1.9] font-medium max-w-2xl"
              data-testid="about-arabic"
            >
              <p>
                أنا <span className="text-[#FD1843] font-bold">باسط لؤي</span>،
                مصمم جرافيك ومدرب استخدام أدوات الذكاء الاصطناعي والفوتوشوب،
                ومختص بتصميم المنتجات، أنا من العراق. أعمل على تصميم الحملات
                الإعلانية وتصاميم السوشيال ميديا والإعلانات البصرية الاحترافية
                التي تساعد العلامات التجارية على التميز وبناء حضور قوي.
              </p>
              <p>
                كما أقدّم دورات تدريبية في الفوتوشوب والذكاء الاصطناعي لمساعدة
                المصممين على تطوير مهاراتهم وبناء بورتفوليو احترافي والعمل مع
                العملاء بشكل احترافي.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
